import { useLocation, Link } from 'react-router-dom';
import { articles as mockArticles } from '@/mocks/articles';
import {
  relatedInsights,
  getServicePageRelated,
  getResourceRelated,
  toServiceLinks,
  CATEGORY_SERVICES,
  DEFAULT_SERVICES,
  type RelatedLink,
} from '@/lib/relatedContent';
import { SERVICE_LINE_LIST } from '@/lib/services/lines';
import { landingsFor } from '@/lib/services/landings';
import { relatedIcon } from '@/lib/relatedIcons';

/** Service-line pages (Infor, Workday, staffing, AI, development) link to their pricing calculator and landing pages. */
const LINE_SCOPES: { prefixes: string[]; id: (typeof SERVICE_LINE_LIST)[number]['id'] }[] = [
  { prefixes: ['/services/erp/infor', '/services/erp/managed-services'], id: 'infor' },
  { prefixes: ['/services/erp/workday'], id: 'workday' },
  { prefixes: ['/services/staffing'], id: 'staffing' },
  { prefixes: ['/services/ai'], id: 'ai' },
  { prefixes: ['/services/app'], id: 'dev' },
];

function linePricingLinks(path: string): RelatedLink[] {
  const scope = LINE_SCOPES.find((x) => x.prefixes.some((p) => path.startsWith(p)));
  if (!scope) return [];
  const line = SERVICE_LINE_LIST.find((l) => l.id === scope.id)!;
  if (path === line.pricingPath) return [];
  return [
    { to: line.pricingPath, label: `${line.short} pricing calculator`, desc: line.answerTitle },
    ...landingsFor(line.id).map((l) => ({ to: `${line.landingPrefix}${l.slug}`, label: l.h1, desc: l.description.slice(0, 110) + '…' })),
  ];
}

/**
 * Site-wide internal-link helper. Rendered once in both the client shell
 * (src/App.tsx) and the SSR shell (src/entry-server.tsx) so the links exist in
 * the crawlable HTML:
 *   • every service page links to its related how-to videos and insights
 *   • every insight/article links to 2–3 relevant service pages + 2 insights
 *   • every resource page links to 2–3 relevant service pages + 2 insights
 * It renders nothing on all other routes.
 *
 * The link tables live in src/lib/relatedContent.ts so the prerenderer can emit
 * the exact same links into its deterministic fallback blocks.
 */

function LinkGrid({ title, links }: { title: string; links: RelatedLink[] }) {
  if (!links.length) return null;
  return (
    <div>
      <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-400 mb-4">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {links.map((l) => (
          <Link
            key={l.to + l.label}
            to={l.to}
            className="group flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 hover:border-teal-300 hover:bg-teal-50/40 transition-colors cursor-pointer"
          >
            <img src={relatedIcon(l)} alt="" width={48} height={48} loading="lazy" decoding="async" className="w-12 h-12 rounded-xl shrink-0 shadow-sm group-hover:scale-105 transition-transform" />
            <div>
              <span className="block font-semibold text-slate-900 text-sm group-hover:text-teal-700 leading-snug">{l.label}</span>
              <span className="block text-xs text-slate-500 mt-0.5 leading-relaxed">{l.desc}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function RouteRelatedContent() {
  const { pathname } = useLocation();
  const clean = pathname.replace(/\/+$/, '') || '/';

  // ── Service pages: related how-to videos + related insights ──
  if (clean.startsWith('/services')) {
    const { videos, insights } = getServicePageRelated(clean);
    const pricing = linePricingLinks(clean);
    if (!videos.length && !insights.length && !pricing.length) return null;
    return (
      <section className="py-14 bg-white border-t border-slate-100" aria-label="Related content">
        <div className="max-w-7xl mx-auto px-6 space-y-10">
          <LinkGrid title="Pricing and options" links={pricing} />
          <LinkGrid title="Related how-to videos" links={videos} />
          <LinkGrid title="Related insights" links={insights} />
        </div>
      </section>
    );
  }

  // ── Insight/article pages: 2–3 relevant service pages + 2 related insights ──
  if (clean.startsWith('/insights/') && clean.length > '/insights/'.length) {
    const slug = clean.slice('/insights/'.length);
    const article = mockArticles.find((a) => a.slug === slug);
    const paths = (article && CATEGORY_SERVICES[article.category]) || DEFAULT_SERVICES;
    const serviceLinks = toServiceLinks(paths).slice(0, 3);
    const insightLinks = relatedInsights(article?.category, slug);
    if (!serviceLinks.length && !insightLinks.length) return null;
    return (
      <section className="py-14 bg-white border-t border-slate-100" aria-label="Related content">
        <div className="max-w-7xl mx-auto px-6 space-y-10">
          <LinkGrid title="Related Cendien services" links={serviceLinks} />
          <LinkGrid title="Related insights" links={insightLinks} />
        </div>
      </section>
    );
  }

  // ── Resource pages: 2–3 relevant service pages + 2 related insights ──
  if (clean.startsWith('/resources/') && clean.length > '/resources/'.length) {
    const slug = clean.slice('/resources/'.length);
    const { services, insights } = getResourceRelated(slug);
    if (!services.length && !insights.length) return null;
    return (
      <section className="py-14 bg-white border-t border-slate-100" aria-label="Related content">
        <div className="max-w-7xl mx-auto px-6 space-y-10">
          <LinkGrid title="Related Cendien services" links={services} />
          <LinkGrid title="Related insights" links={insights} />
        </div>
      </section>
    );
  }

  return null;
}